import React from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';

import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Text } from '@/components/TextComponent';
import { Box, Image } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { SCROLLBAR_WIDTH } from '@/feature/responsive-design/WebComponents';
import { designSystem } from '@/styles/styles';

import { RipsCategory } from '../hooks/use-rips-games';
import { RIPS_MAX_ROW_WIDTH, RIPS_ROW_PADDING } from '../hooks/use-rips-row-item-width';

const CHIP_GAP = 12;
const CHIP_HEIGHT = 60;
const CHIP_RADIUS = 14;
const CHIP_TEXTS_PADDING_LEFT = 16;
const IMAGE_FRAME_WIDTH = 48;
const IMAGE_SIZE = 60;
const IMAGE_TOP = 10;
const SKELETON_CHIPS = 4;

type CategoryChipProps = {
    category: RipsCategory;
    width: number;
    onPress: (category: RipsCategory) => void;
};

const CategoryChip = ({ category, width, onPress }: CategoryChipProps) => (
    <TouchableOpacity
        onPress={() => onPress(category)}
        activeOpacity={0.8}
        style={[styles.chip, { width }]}
        testID={`ripsCategoryChip-${category.gameId}`}
    >
        <Text variant="labelLarge2" color={ds.colorToken.text.primary} numberOfLines={1}>
            {category.name}
        </Text>
        <Text variant="labelLarge2" color={ds.colorToken.text.secondary} numberOfLines={1}>
            {`${category.packs.length} ${category.packs.length === 1 ? 'pack' : 'packs'}`}
        </Text>
        <Box style={styles.imageFrame}>
            <Image source={{ uri: category.image }} style={styles.image} />
        </Box>
    </TouchableOpacity>
);

type RipsCategoriesProps = {
    categories: RipsCategory[];
    isLoading?: boolean;
    onCategoryPress: (category: RipsCategory) => void;
};

/**
 * "Categories" section of the Rips lobby: two rows of chips filled column by column, each opening that category in the Rips web app.
 */
export const RipsCategories = ({ categories, isLoading, onCategoryPress }: RipsCategoriesProps) => {
    const { width } = useWindowDimensions();
    const chipWidth = Math.floor(
        (Math.min(width - SCROLLBAR_WIDTH, RIPS_MAX_ROW_WIDTH) - 2 * RIPS_ROW_PADDING - CHIP_GAP) / 2
    );

    if (isLoading) {
        return (
            <Box style={[styles.list, styles.grid]} mb="s24" testID="ripsCategoriesSkeleton">
                {[...Array(SKELETON_CHIPS).keys()].map(index => (
                    <LoadingSkeleton
                        key={index}
                        width={chipWidth}
                        height={CHIP_HEIGHT}
                        borderRadius="r14"
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        skeletonColor={designSystem.colors.neutral300}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box pb="s24">
            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={[styles.list, styles.grid]}
            >
                {categories.map(category => (
                    <CategoryChip
                        key={category.gameId}
                        category={category}
                        width={chipWidth}
                        onPress={onCategoryPress}
                    />
                ))}
            </ScrollView>
        </Box>
    );
};

const styles = StyleSheet.create({
    list: {
        paddingHorizontal: RIPS_ROW_PADDING,
    },
    grid: {
        height: CHIP_HEIGHT * 2 + CHIP_GAP,
        flexDirection: 'column',
        flexWrap: 'wrap',
        gap: CHIP_GAP,
    },
    chip: {
        height: CHIP_HEIGHT,
        justifyContent: 'center',
        paddingLeft: CHIP_TEXTS_PADDING_LEFT,
        paddingRight: IMAGE_FRAME_WIDTH,
        borderRadius: CHIP_RADIUS,
        borderCurve: 'continuous',
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        overflow: 'hidden',
    },
    imageFrame: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: IMAGE_FRAME_WIDTH,
        height: CHIP_HEIGHT,
        overflow: 'hidden',
    },
    image: {
        position: 'absolute',
        left: 0,
        top: IMAGE_TOP,
        width: IMAGE_SIZE,
        height: IMAGE_SIZE,
    },
});
